import React, { useState, useRef } from 'react';
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Link,
  Quote,
  Eye,
  EyeOff,
} from 'lucide-react';

interface MarkdownEditorProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  required?: boolean;
  hint?: string;
}

export function MarkdownEditor({
  label,
  value,
  onChange,
  placeholder,
  rows = 6,
  required,
  hint,
}: MarkdownEditorProps) {
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertFormatting = (before: string, after: string = '', placeholder: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const textToInsert = selectedText || placeholder;

    const newValue =
      value.substring(0, start) +
      before +
      textToInsert +
      after +
      value.substring(end);

    onChange(newValue);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + textToInsert.length;
      textarea.setSelectionRange(
        selectedText ? newCursorPos + after.length : start + before.length,
        selectedText ? newCursorPos + after.length : start + before.length + textToInsert.length
      );
    }, 0);
  };

  const insertAtLineStart = (prefix: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;

    const newValue =
      value.substring(0, lineStart) +
      prefix +
      value.substring(lineStart);

    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, start + prefix.length);
    }, 0);
  };

  const formatButtons = [
    { icon: Bold, title: 'Bold (Ctrl+B)', action: () => insertFormatting('**', '**', 'text bold') },
    { icon: Italic, title: 'Italic (Ctrl+I)', action: () => insertFormatting('_', '_', 'text italic') },
    { icon: Heading1, title: 'Heading 1', action: () => insertAtLineStart('# ') },
    { icon: Heading2, title: 'Heading 2', action: () => insertAtLineStart('## ') },
    { icon: List, title: 'Lista cu buline', action: () => insertAtLineStart('- ') },
    { icon: ListOrdered, title: 'Lista numerotata', action: () => insertAtLineStart('1. ') },
    { icon: Quote, title: 'Citat', action: () => insertAtLineStart('> ') },
    { icon: Link, title: 'Link', action: () => insertFormatting('[', '](url)', 'text link') },
  ];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'b') {
        e.preventDefault();
        insertFormatting('**', '**', 'text bold');
      } else if (e.key === 'i') {
        e.preventDefault();
        insertFormatting('_', '_', 'text italic');
      }
    }
  };

  // Simple Markdown to HTML converter
  const renderMarkdown = (text: string): string => {
    if (!text) return '<p class="text-gray-400 italic">Nimic de previzualizat</p>';

    let html = text
      // Escape HTML
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Headers
      .replace(/^### (.+)$/gm, '<h4 class="text-base font-bold mt-3 mb-1">$1</h4>')
      .replace(/^## (.+)$/gm, '<h3 class="text-lg font-bold mt-3 mb-1">$1</h3>')
      .replace(/^# (.+)$/gm, '<h2 class="text-xl font-bold mt-3 mb-2">$1</h2>')
      // Bold and Italic
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      // Links
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-primary-600 underline">$1</a>')
      // Blockquotes
      .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-gray-300 pl-3 italic text-gray-600">$1</blockquote>')
      // Lists
      .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
      .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4"><span class="font-medium">$1.</span> $2</li>')
      // Line breaks
      .replace(/\n\n/g, '</p><p class="mt-2">')
      .replace(/\n/g, '<br/>');

    return `<p>${html}</p>`;
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}

      <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
        {/* Toolbar */}
        <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
          {formatButtons.map((btn, index) => (
            <button
              key={index}
              type="button"
              onClick={btn.action}
              title={btn.title}
              className="p-1.5 rounded hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <btn.icon className="w-4 h-4" />
            </button>
          ))}

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            title={showPreview ? 'Editare' : 'Previzualizare'}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
              showPreview
                ? 'bg-primary-100 text-primary-700'
                : 'hover:bg-gray-200 text-gray-600'
            }`}
          >
            {showPreview ? (
              <>
                <EyeOff className="w-3.5 h-3.5" />
                Editare
              </>
            ) : (
              <>
                <Eye className="w-3.5 h-3.5" />
                Preview
              </>
            )}
          </button>
        </div>

        {/* Editor / Preview */}
        {showPreview ? (
          <div
            className="p-3 min-h-[150px] bg-white prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }}
          />
        ) : (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={rows}
            required={required}
            className="w-full px-3 py-2 text-sm border-0 focus:ring-0 resize-none"
            style={{ minHeight: `${rows * 1.5}rem` }}
          />
        )}
      </div>

      {hint && (
        <p className="text-xs text-gray-500 mt-1">
          {hint} Suportă formatare Markdown (compatibil OpenProject).
        </p>
      )}
    </div>
  );
}
